<script lang="ts">
/** Anything inside the dialog that Tab can reach. */
const FOCUSABLE = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled]):not([type=hidden])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  "[tabindex]:not([tabindex='-1'])",
].join(",");

/**
 * How many dialogs are open. Counted because two can be: a message dialog and a
 * confirm, and the first one closing must not hand the page back while the
 * second is still up.
 */
let openDialogs = 0;

/**
 * Takes the page out of play behind a dialog. The focus trap already stops Tab
 * leaving; `inert` is what stops a screen reader's virtual cursor browsing the
 * content behind, and what makes `aria-modal="true"` true rather than claimed.
 */
function lockBackground() {
  openDialogs += 1;
  if (openDialogs === 1) document.getElementById("app")?.setAttribute("inert", "");
}

function unlockBackground() {
  openDialogs = Math.max(0, openDialogs - 1);
  if (openDialogs === 0) document.getElementById("app")?.removeAttribute("inert");
}
</script>

<script setup lang="ts">
import { nextTick, onBeforeUnmount, ref, watch } from "vue";
import router from "../router";
import LoadingSpinner from "./loading-spinner.vue";

// One label/value row of the optional detail list.
interface ModalDetail {
  label: string;
  value: string;
}

const props = withDefaults(
  defineProps<{
    modelValue: boolean;
    title: string;
    // Plain text, NOT markup. Rendered through {{ }} so anything reaching it -
    // API error strings, user emails, URL query values - cannot inject script.
    // Line breaks: embed "\n"; the .modal-message rule below honours them.
    message: string;
    isLoading: boolean;
    // Optional label/value rows rendered as an escaped list under the message.
    details?: ModalDetail[];
    // Optional: enables the Yes/No confirm mode; plain message
    // modals leave it defaulted to false.
    isProceed?: boolean;
    // Confirm mode only: Yes performs a destructive action (e.g. delete),
    // so it renders as a PhilaUI danger button instead of primary.
    isDestructive?: boolean;
    routeName?: string;
  }>(),
  {
    modelValue: false,
    title: "",
    message: "",
    isLoading: false,
    details: () => [],
    isProceed: false,
    isDestructive: false,
    routeName: "",
  },
);

const emit = defineEmits<{
  (e: "update:modelValue", value: boolean): void;
  (e: "proceed"): void;
}>();

// The template also passes its session token on this redirect; XGuard signs in
// through MSAL and has none to pass.
function close() {
  if (props.routeName) {
    router.push({ name: props.routeName });
  }
  emit("update:modelValue", false);
}

function handleProceed() {
  emit("proceed");
}

// XGuard addition: the accessibility the PhilaUI modal leaves out. On open,
// focus moves into the dialog and the page behind goes inert; Tab cycles
// inside, Escape closes, and on close focus returns to whatever opened it.
const dialog = ref<HTMLElement | null>(null);
let openerElement: HTMLElement | null = null;
let isOpen = false;

function onOpen() {
  isOpen = true;
  openerElement =
    document.activeElement instanceof HTMLElement ? document.activeElement : null;
  lockBackground();
  // tabindex="-1" on the wrapper so it can take focus: a screen reader then
  // reads the dialog and its title before its buttons.
  nextTick(() => dialog.value?.focus());
}

function onClosed() {
  isOpen = false;
  // Released before focus is restored, not after: the opener is inside the
  // page, and focusing an element that is still inert does nothing.
  unlockBackground();
  const opener = openerElement;
  openerElement = null;
  if (opener && document.contains(opener)) opener.focus();
}

watch(
  () => props.modelValue,
  (open) => {
    if (open && !isOpen) onOpen();
    else if (!open && isOpen) onClosed();
  },
  { immediate: true },
);

onBeforeUnmount(() => {
  if (isOpen) onClosed();
});

function focusableItems(): HTMLElement[] {
  if (!dialog.value) return [];
  return [...dialog.value.querySelectorAll<HTMLElement>(FOCUSABLE)].filter(
    (el) => el.offsetParent !== null,
  );
}

/** Escape closes, and Tab cycles within the dialog. */
function onKeydown(event: KeyboardEvent) {
  if (event.key === "Escape") {
    event.stopPropagation();
    close();
    return;
  }

  if (event.key !== "Tab") return;

  const items = focusableItems();
  if (items.length === 0) {
    event.preventDefault();
    return;
  }

  const first = items[0];
  const last = items[items.length - 1];
  const active = document.activeElement;

  if (event.shiftKey && (active === first || active === dialog.value)) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && active === last) {
    event.preventDefault();
    first.focus();
  }
}
</script>
<template>
  <!-- XGuard additions: PhilaUI's modal-window carries no dialog role or name,
       and screen readers and the e2e suite find a dialog by both. Teleported to
       <body> so the dialog is a sibling of #app, which it marks inert. -->
  <Teleport to="body">
    <div
      v-show="modelValue"
      ref="dialog"
      class="modalWrapper"
      role="dialog"
      aria-modal="true"
      :aria-label="title"
      tabindex="-1"
      @keydown="onKeydown"
    >
      <modal
        type="none"
        :hide-close-button="true"
        :close="close"
      >
        <template #title>
          {{ title }}
        </template>

        <div
          v-if="!isLoading"
          class="content"
        >
          <p
            v-if="message"
            class="modal-message"
          >
            {{ message }}
          </p>
          <ul v-if="details.length">
            <li
              v-for="detail in details"
              :key="detail.label"
            >
              <strong>{{ detail.label }}:</strong> {{ detail.value }}
            </li>
          </ul>
        </div>

        <div
          v-else
          class="content"
        >
          <LoadingSpinner :loading="true" />
        </div>

        <template #actions-before>
          <button
            v-if="!isProceed"
            class="button is-primary"
            @click="close"
          >
            Close
          </button>
          <button
            v-if="isProceed"
            class="button is-secondary"
            @click="close"
          >
            No
          </button>
          <button
            v-if="isProceed"
            class="button"
            :class="isDestructive ? 'is-danger' : 'is-primary'"
            @click="handleProceed"
          >
            Yes
          </button>
        </template>
      </modal>
    </div>
  </Teleport>
</template>

<style scoped>
/* The wrapper takes focus on open only so the dialog is announced; it is not a
   control, so it draws no ring. */
.modalWrapper:focus {
  outline: none;
}

/* Callers pass plain text with "\n" where they would otherwise pass <br />. */
.modal-message {
  white-space: pre-line;
}

/* PhilaUI forces overflow-y: scroll, drawing a scrollbar on every modal;
   only show one when the message is taller than the window. */
.modalWrapper :deep(.modal-window .modal-window-content) {
  overflow-y: auto;
}

/* PhilaUI + Bulma margins put ~68px between No and Yes; use the same
   11px gap as the button rows on the pages. */
.modalWrapper :deep(.modal-window-actions .buttons) {
  column-gap: 11px;
}
.modalWrapper :deep(.modal-window-actions .buttons .button) {
  margin-left: 0 !important;
  margin-right: 0 !important;
}
</style>
